import { cn } from '@bridebook/aura';
import { ImageX } from '@bridebook/ui';
import {
  GLAMOUR_ACCENTS,
  Palette,
  PALETTE_ACCENTS,
  SKETCHES_ACCENTS,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { useThemeContext } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';
import { toMainPalette } from '../../config/utils';

const getExtraTopSpacingClassName = (palette: Palette) => {
  switch (palette) {
    case Palette.Timeless:
      return 'h-[40px]';
    case Palette.Blossom:
      return 'h-[80px]';
    default:
      return '';
  }
};

/**
 * Desktop accents for Split layout, positioned relative to the wedding overview section.
 * Only visible on desktop (@screen-lg:block).
 * Mirrors the mobile accent approach but for desktop viewports.
 *
 * Note: Bridebook swirls are handled by SplitLayoutAccent at the root level.
 */
export const SplitDesktopAccent = () => {
  const { palette } = useThemeContext();
  const mainPalette = toMainPalette(palette);

  if (mainPalette === Palette.Bridebook) return null;

  return (
    <>
      {/* Some of the themes (Timeless, Blossom, Amore) need extra top spacing */}
      <div
        id="extra-top-spacing"
        className={cn(
          'pointer-events-none hidden @screen-lg:block',
          getExtraTopSpacingClassName(palette),
        )}></div>
      <div
        className="pointer-events-none absolute inset-0 z-10 hidden @screen-lg:block"
        aria-hidden="true">
        <AccentElement />
      </div>
    </>
  );
};

const AccentElement = () => {
  const { palette } = useThemeContext();

  switch (palette) {
    case Palette.Bridebook:
    case Palette.BridebookGreen:
    case Palette.BridebookBlush:
      return null;

    case Palette.Roses:
    case Palette.RosesPlum:
    case Palette.RosesBrown:
      return (
        <>
          <div className="absolute top-0 left-0" style={{ width: 120, height: 200 }}>
            <ImageX w={120} h={200} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
          <div
            className="absolute right-0 bottom-0 scale-x-[-1] scale-y-[-1]"
            style={{ width: 120, height: 200 }}>
            <ImageX w={120} h={200} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
        </>
      );
    case Palette.Foliage:
    case Palette.FoliageOrange:
    case Palette.FoliageBlue:
      return (
        <>
          <div
            className="absolute top-1/2 left-[20px] -translate-y-1/2"
            style={{ width: 75, height: 150 }}>
            <ImageX w={75} h={150} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
          <div
            className="absolute top-1/2 right-[20px] -translate-y-1/2 scale-x-[-1]"
            style={{ width: 75, height: 150 }}>
            <ImageX w={75} h={150} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
        </>
      );
    case Palette.Whimsical:
    case Palette.WhimsicalBeige:
    case Palette.WhimsicalGreen:
      return (
        <>
          <div
            className="absolute top-1/2 left-[10px] -translate-y-1/2"
            style={{ width: 115, height: 160 }}>
            <ImageX w={115} h={160} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
          <div
            className="absolute top-1/2 right-[10px] -translate-y-1/2 scale-x-[-1]"
            style={{ width: 115, height: 160 }}>
            <ImageX w={115} h={160} src={PALETTE_ACCENTS[palette]} fit="cover" altText="" />
          </div>
        </>
      );
    case Palette.Sketches:
    case Palette.SketchesGreen:
    case Palette.SketchesLilac:
      return (
        <>
          <div className="absolute top-0 left-[10px]" style={{ width: 105, height: 337 }}>
            <ImageX
              w={105}
              h={337}
              src={SKETCHES_ACCENTS[palette].mobileLeft}
              fit="contain"
              altText=""
            />
          </div>
          <div className="absolute top-0 right-[10px]" style={{ width: 198, height: 316 }}>
            <ImageX
              w={198}
              h={316}
              src={SKETCHES_ACCENTS[palette].mobileRight}
              fit="contain"
              altText=""
            />
          </div>
        </>
      );
    case Palette.Glamour:
    case Palette.GlamourNavy:
    case Palette.GlamourChampagne:
      return (
        <>
          <div className="absolute top-0 left-[10px]" style={{ width: 150, height: 150 }}>
            <ImageX w={150} h={150} src={GLAMOUR_ACCENTS.desktop} fit="cover" altText="" />
          </div>
          <div
            className="absolute top-0 right-[10px] scale-x-[-1]"
            style={{ width: 150, height: 150 }}>
            <ImageX w={150} h={150} src={GLAMOUR_ACCENTS.desktop} fit="cover" altText="" />
          </div>
        </>
      );
    case Palette.Timeless:
      return (
        <div className="absolute top-[18px] left-1/2 h-[52px] w-[320px] -translate-x-1/2">
          <ImageX w={320} h={52} src={PALETTE_ACCENTS[Palette.Timeless]} fit="cover" />
        </div>
      );
    case Palette.Blossom:
      return (
        <div className="absolute top-[20px] left-1/2 h-[112px] w-[84px] -translate-x-1/2">
          <ImageX w={84} h={112} src={PALETTE_ACCENTS[Palette.Blossom]} fit="cover" />
        </div>
      );
    case Palette.Amore:
      return (
        <>
          <div className="absolute top-[22px] left-[10px] h-[190px] w-[190px]">
            <ImageX w={190} h={190} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
          <div className="absolute top-[22px] right-[10px] h-[190px] w-[190px] scale-x-[-1]">
            <ImageX w={190} h={190} src={PALETTE_ACCENTS[palette]} fit="cover" />
          </div>
        </>
      );
    default:
      return null;
  }
};
